import {
  isApiUrl,
  isTwoStepRequiredBody,
  PROOF_HEADER,
  TWO_STEP_REQUIRED_EVENT,
} from "../../features/twoStep/twoStep.utils";
import { currentProof } from "../../features/twoStep/twoStepStorage";

/**
 * Two-step sign-in (Roadmap 1845): adds the X-Proc-2fa proof to EVERY call to
 * our API, in one place. The app talks to the API in many ways (RTK Query
 * slices, Apollo GraphQL, axios, plain fetch, XHR uploads, SignalR's
 * negotiate), and most of them copy only the Authorization header from
 * getAuthHeader(), so the header is added at the browser's fetch /
 * XMLHttpRequest level instead, and only for URLs on our API.
 *
 * It also notices the API's 403 {code: "two_factor_required"} and fires the
 * window event TWO_STEP_REQUIRED_EVENT, so the two-step screen shows again.
 *
 * Calls to anything else (Microsoft sign-in, Nylas, maps...) are untouched.
 */

let installed = false;

const notifyRequired = () => {
  try {
    window.dispatchEvent(new Event(TWO_STEP_REQUIRED_EVENT));
  } catch {
    // very old browsers: ignore
  }
};

const urlOf = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return (input as Request).url;
};

export const installTwoStepHeader = (apiBase: string) => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = urlOf(input);
      if (!isApiUrl(url, apiBase)) return originalFetch(input, init);
      const proof = currentProof();
      let nextInit = init;
      if (proof) {
        const headers = new Headers(
          init?.headers ||
            (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has(PROOF_HEADER)) headers.set(PROOF_HEADER, proof);
        nextInit = { ...(init || {}), headers };
      }
      const response = await originalFetch(input, nextInit);
      if (response.status === 403) {
        response
          .clone()
          .text()
          .then((text) => {
            if (isTwoStepRequiredBody(text)) notifyRequired();
          })
          .catch(() => undefined);
      }
      return response;
    };
  }

  const XHR = window.XMLHttpRequest;
  if (XHR?.prototype) {
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    type Tagged = XMLHttpRequest & { __procApi?: boolean };
    XHR.prototype.open = function (
      this: Tagged,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      this.__procApi = isApiUrl(String(url), apiBase);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (open as any).call(this, method, url, ...rest);
    } as typeof XHR.prototype.open;
    XHR.prototype.send = function (
      this: Tagged,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      if (this.__procApi) {
        const proof = currentProof();
        if (proof) {
          try {
            this.setRequestHeader(PROOF_HEADER, proof);
          } catch {
            // already sent / not opened: leave it
          }
        }
        this.addEventListener("loadend", () => {
          if (this.status !== 403) return;
          try {
            const text =
              this.responseType === "" || this.responseType === "text"
                ? this.responseText
                : this.responseType === "json"
                ? this.response
                : "";
            if (isTwoStepRequiredBody(text)) notifyRequired();
          } catch {
            // unreadable body: ignore
          }
        });
      }
      return send.call(this, body);
    };
  }
};
